import { IQuestionnaireDetailsSchema } from "@Totum/QuestionnaireBuilder/Domain/schemas";
import {
    IQuestionnaireDetails,
    IQuestionnaireDomain,
    IUpdatedAssociations,
} from "@Totum/QuestionnaireBuilder/Domain/types";
import { createEmptyObjectFromSchema } from "utils/zod";
import { useMemo } from "react";
import { createNewQuestion, isValidQuestionType } from "@Totum/QuestionnaireBuilder/Domain/questionnaireMethods";
import { DefaultKeys } from "@Totum/QuestionnaireBuilder/Constants";

/**
 * Represents the domain logic for a questionnaire.
 */
class QuestionnaireDomain implements IQuestionnaireDomain {
    private _questionnaireDetails: IQuestionnaireDetails;

    constructor(
        questionnaireDetails: IQuestionnaireDetails = createEmptyObjectFromSchema(IQuestionnaireDetailsSchema, {
            questionnaireId: undefined,
        })
    ) {
        this._questionnaireDetails = {
            ...questionnaireDetails,
            points: questionnaireDetails.points === 0 ? undefined : questionnaireDetails.points,
            learningObjectiveDetails: questionnaireDetails.learningObjectiveDetails ?? {
                description: "",
                learningObjectiveId: "",
            },
        };
    }

    // ** Getters ** //
    public get details(): IQuestionnaireDetails {
        return this._questionnaireDetails;
    }

    // ** Booleans **/

    public get hasQuestions(): boolean {
        return Boolean(this._questionnaireDetails.questions.length);
    }

    public get isExistingQuestionnaire(): boolean {
        return Boolean(
            this._questionnaireDetails.questionnaireId &&
                this._questionnaireDetails.questionnaireId !== String(DefaultKeys.NEW_QUESTIONNAIRE_ID)
        );
    }

    // ** Methods ** //

    public updateTitleAndDescription(title: string, description: string): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            title,
            description,
        };
    }

    public updatePointsAssociation(points: number): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            points,
        };
    }

    public updateAssociations({
        activitiesToAdd,
        categoriesToAdd,
        activitiesToRemove,
        categoriesToRemove,
    }: IUpdatedAssociations): void {
        this._addAssociatedCategories(categoriesToAdd);
        this._removeAssociatedCategories(categoriesToRemove);
        this._addAssociatedActivities(activitiesToAdd);
        this._removeAssociatedActivities(activitiesToRemove);
    }

    public updateLearningObjectives(description: string): void {
        this._questionnaireDetails.learningObjectiveDetails = this._questionnaireDetails.learningObjectiveDetails
            ? {
                  ...this._questionnaireDetails.learningObjectiveDetails,
                  description,
              }
            : {
                  description,
                  learningObjectiveId: "",
              };
    }

    public addQuestion(): void {
        this._questionnaireDetails.questions.push(createNewQuestion(this._questionnaireDetails.questionnaireId));
    }

    public removeQuestion(questionId: string): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.filter(
            (question) => question.questionId !== questionId
        );
    }

    public updateChoices(questionId: string, values: string[]): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.map((question) =>
            question.questionId === questionId
                ? {
                      ...question,
                      choices: values.map((choice, index) => ({
                          choiceId: question.choices[index]?.choiceId ?? index.toString(),
                          isCorrect: question.choices[index]?.isCorrect ?? false,
                          choiceText: choice,
                          orderIndex: index,
                      })),
                  }
                : question
        );
    }

    public updateCorrectChoice(questionId: string, choiceText: string): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.map((question) =>
            question.questionId === questionId
                ? {
                      ...question,
                      choices: question.choices.map((choice) => ({
                          ...choice,
                          isCorrect: choice.choiceText === choiceText,
                      })),
                  }
                : question
        );
    }

    public updateQuestionIsScored(questionId: string, isScored: boolean): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.map((question) =>
            question.questionId === questionId ? { ...question, isScored } : question
        );
    }

    public updateQuestionTitle(questionId: string, title: string): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.map((question) =>
            question.questionId === questionId ? { ...question, title } : question
        );
    }

    public updateQuestionType(questionId: string, type: string): void {
        this._questionnaireDetails.questions = this._questionnaireDetails.questions.map((question) =>
            question.questionId === questionId
                ? { ...question, questionType: isValidQuestionType(type) ? type : question.questionType }
                : question
        );
    }

    // ** Private Methods ** //

    private _addAssociatedCategories(categories: string[]): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            associationCollection: {
                ...this._questionnaireDetails.associationCollection,
                activityCategories: [
                    ...this._questionnaireDetails.associationCollection.activityCategories,
                    ...categories,
                ],
            },
        };
    }

    private _removeAssociatedCategories(categories: string[]): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            associationCollection: {
                ...this._questionnaireDetails.associationCollection,
                activityCategories: this._questionnaireDetails.associationCollection.activityCategories.filter(
                    (c) => !categories.includes(c)
                ),
            },
        };
    }

    private _addAssociatedActivities(activities: string[]): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            associationCollection: {
                ...this._questionnaireDetails.associationCollection,
                activities: [...this._questionnaireDetails.associationCollection.activities, ...activities],
            },
        };
    }

    private _removeAssociatedActivities(activities: string[]): void {
        this._questionnaireDetails = {
            ...this._questionnaireDetails,
            associationCollection: {
                ...this._questionnaireDetails.associationCollection,
                activities: this._questionnaireDetails.associationCollection.activities.filter(
                    (a) => !activities.includes(a)
                ),
            },
        };
    }

    public validate(): boolean {
        const hasActivities = Boolean(
            this._questionnaireDetails.associationCollection.activities.length ||
                this._questionnaireDetails.associationCollection.activityCategories.length
        );
        return Boolean(
            this._questionnaireDetails.title &&
                this._questionnaireDetails.description &&
                this._questionnaireDetails.points &&
                hasActivities
        );
    }

    public validateQuestion(questionId: string): boolean {
        const question = this._questionnaireDetails.questions.find((q) => q.questionId === questionId);

        return !!(question?.title && question?.questionType);
    }

    public toQuestionnaireDetails(): IQuestionnaireDetails {
        return this._questionnaireDetails;
    }
}

export const useQuestionnaireDomain = (initialDetails?: IQuestionnaireDetails): IQuestionnaireDomain => {
    return useMemo(() => new QuestionnaireDomain(initialDetails), [initialDetails]);
};

export default useQuestionnaireDomain;
